import { useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { usePatientDocuments } from '@/patients/services/use-patient-documents';
import { fileUrlFor, type LabResultGroup } from '@/patients/lib/lab-results';
import { ChartSectionHeader } from '../chart-section-header';
import { ChartShowAll } from '../chart-row-cap';
import { useRowCap } from '../use-row-cap';
import { LabRequestCard, type ViewReportTarget } from './lab-request-card';
import { LabResultsContext } from './lab-results-context';

interface LabResultsSectionProps {
  patientId: string;
  groups: LabResultGroup[];
  /** Outside reports with no lab request: their own cards after the ordered ones. */
  externalGroups?: LabResultGroup[];
  /** Root report id to performing lab, for the trend join. */
  labsByRoot?: ReadonlyMap<string, string>;
  /** Lab requests whose results could not be read (not a 404). */
  failed?: { labRequestId: string; name: string }[];
}

/** Enough to find a lab PDF among a patient's documents in one page. */
const DOCUMENT_LOOKUP_PAGE_SIZE = 100;

/**
 * Chart › Labs results, grouped by lab request (PROD-2643).
 *
 * "View report" opens the PDF the way the Documents tab does: the file's (signed) URL in a new
 * tab with noopener. The lab desk list already signs the URL of the PDF linked on the request;
 * a report PDF that is not that one is looked up in the patient's documents, fetched up front so
 * the click opens synchronously (a window.open after an await is popup-blocked).
 */
export function LabResultsSection({
  patientId,
  groups,
  externalGroups = [],
  labsByRoot,
  failed = [],
}: LabResultsSectionProps) {
  const popoverHost = useRef<HTMLDivElement>(null);
  // The 5 latest groups (the order the request list already has), the rest behind "Show all N"; outside reports the same.
  const groupCap = useRowCap(groups);
  const externalCap = useRowCap(externalGroups);
  const context = useMemo(
    () => ({ labsByRoot: labsByRoot ?? new Map<string, string>(), popoverHost }),
    [labsByRoot],
  );
  const needsLookup = [...groups, ...externalGroups].some((g) =>
    g.reports.some((r) => r.patientDocumentId && r.patientDocumentId !== g.requestDocumentId),
  );
  const docs = usePatientDocuments({
    patientId: needsLookup ? patientId : null,
    pageSize: DOCUMENT_LOOKUP_PAGE_SIZE,
  });

  const onViewReport = (target: ViewReportTarget) => {
    const url = target.url || fileUrlFor(docs.data, target.patientDocumentId);
    if (!url) {
      toast.error('Could not open the report. Find it under Documents.');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <LabResultsContext.Provider value={context}>
    <div ref={popoverHost} className="relative space-y-3" data-testid="lab-results-section">
      <ChartSectionHeader
        title="Lab results"
        count={groups.length + externalGroups.length}
        showAdd={false}
      />
      {failed.map((f) => (
        <p
          key={f.labRequestId}
          className="rounded-lg border border-border-default px-3 py-2 text-sm text-status-error"
          role="alert"
          data-testid="lab-results-failed"
        >
          Could not load results for {f.name}. Try again later.
        </p>
      ))}
      {groupCap.visible.map((g) => (
        <LabRequestCard
          key={g.labRequestId}
          patientId={patientId}
          group={g}
          onViewReport={onViewReport}
          documentsLoading={needsLookup && !docs.data}
        />
      ))}
      <ChartShowAll cap={groupCap} label="lab results" />
      {externalGroups.length > 0 ? (
        <>
          <h3 className="text-sm font-medium text-text-secondary" data-testid="lab-outside-heading">
            Outside reports (no order)
          </h3>
          {externalCap.visible.map((g) => (
            <LabRequestCard
              key={g.labRequestId}
              patientId={patientId}
              group={g}
              onViewReport={onViewReport}
              documentsLoading={needsLookup && !docs.data}
            />
          ))}
          <ChartShowAll cap={externalCap} label="outside reports" />
        </>
      ) : null}
    </div>
    </LabResultsContext.Provider>
  );
}
